import { durableNetworkFallback } from "../durableNetworkFallback";
import type { DataSession } from "../../../data/core/session";
import type { z } from "zod";
import { NoAuthError } from "@utils/NoAuthError";
import { trpc, RouterOutputs } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import { EmptyServerResponseError } from "@utils/durableCache";
import { playlistWithSongsSchema } from "@libs/wire";

/**
 * Compile-time drift check for `catalog.playlists.getTodaysTopHits` —
 * `playlistWithSongsSchema` covers the same shape as the router output
 * (the route's local `todaysTopHitsPlaylistSchema` is structurally
 * `playlistSchema.extend({ songs: z.array(songSchema) })`, which is
 * exactly `playlistWithSongsSchema`).
 */
type TodaysTopHitsRouterOutput =
  RouterOutputs["catalog"]["playlists"]["getTodaysTopHits"];
type _TodaysTopHitsWireSchemaSubset =
  z.infer<typeof playlistWithSongsSchema> extends TodaysTopHitsRouterOutput
    ? true
    : never;
type _TodaysTopHitsWireSchemaSuperset =
  TodaysTopHitsRouterOutput extends z.infer<typeof playlistWithSongsSchema>
    ? true
    : never;
const _todaysTopHitsWireSchemaDriftCheck: [
  _TodaysTopHitsWireSchemaSubset,
  _TodaysTopHitsWireSchemaSuperset,
] = [true, true];
void _todaysTopHitsWireSchemaDriftCheck;

export const todaysTopHitsQueryKey = () =>
  getQueryKey(trpc.catalog.playlists.getTodaysTopHits);

export const todaysTopHitsQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    fallbackOnError: durableNetworkFallback,
    queryKey: todaysTopHitsQueryKey(),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("todaysTopHitsQuery queryFn");
      }
      return data.api.catalog.playlists.getTodaysTopHits.query();
    },
    schema: playlistWithSongsSchema,
    // Server invariant: today's top hits is always populated. An empty
    // songs array must not overwrite the previous durable value.
    validate: (data) => {
      if (!data?.songs?.length) {
        throw new EmptyServerResponseError(todaysTopHitsQueryKey());
      }
    },
  });
